/* ============================================
   Shared Utilities — Cotizador 1Conecta
   Migrated from app.js + server.js
   Single source of truth (no more duplication)
   ============================================ */

/**
 * Format a number as currency with symbol
 * Source: app.js:15-17 + server.js:157-159
 */
export function formatCurrency(amount: number, currency: string = 'USD'): string {
  return (
    new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount) +
    ' ' +
    currency
  )
}

/**
 * Format a date string (YYYY-MM-DD) to localized format
 * Source: app.js:19-23 + server.js:160-164
 */
export function formatDate(dateStr: string | null): string {
  if (!dateStr) return ''
  const d = new Date(dateStr + 'T00:00:00')
  return d.toLocaleDateString('es-PE', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

/**
 * Determine discount tier based on percentage
 * Source: page-ver-cotizacion.js:7-34 + server.js:165-172
 */
export function getDiscountTier(pct: number): {
  label: string
  emoji: string
  color: string
  description: string
} {
  if (pct <= 0) return { label: 'Sin descuento', emoji: '', color: '', description: '' }
  if (pct <= 5)
    return {
      label: 'Cortesía',
      emoji: '🤝',
      color: '#4CAF50',
      description: 'Descuento de cortesía por relación comercial',
    }
  if (pct <= 10)
    return {
      label: 'Preferencial',
      emoji: '⭐',
      color: '#2196F3',
      description: 'Tarifa preferencial para clientes frecuentes',
    }
  if (pct <= 15)
    return {
      label: 'Aliado',
      emoji: '🤝',
      color: '#FF9800',
      description: 'Precio especial de aliado estratégico',
    }
  if (pct <= 20)
    return {
      label: 'Estratégico',
      emoji: '🎯',
      color: '#9C27B0',
      description: 'Descuento estratégico de alto valor',
    }
  return {
    label: 'VIP',
    emoji: '👑',
    color: '#F44336',
    description: 'Descuento máximo exclusivo VIP',
  }
}

/**
 * Generate WhatsApp link
 * Source: server.js:194-198
 */
export function waLink(phone: string, text: string): string {
  const clean = phone.replace(/[^0-9+]/g, '')
  return `https://wa.me/${clean}?text=${encodeURIComponent(text)}`
}

/**
 * Process content: convert markdown-like syntax to HTML
 * Source: page-ver-cotizacion.js:50-72 + server.js:173-193
 */
export function processContent(text: string): string {
  if (!text) return ''

  return text
    .split('\n')
    .map((line) => {
      const trimmed = line.trim()

      // Headings
      if (trimmed.startsWith('### '))
        return `<h5 style="margin:8px 0 4px;color:#1C4587">${trimmed.slice(4)}</h5>`
      if (trimmed.startsWith('## '))
        return `<h4 style="margin:10px 0 5px;color:#1C4587">${trimmed.slice(3)}</h4>`
      if (trimmed.startsWith('# '))
        return `<h3 style="margin:12px 0 6px;color:#1C4587">${trimmed.slice(2)}</h3>`

      // Bold
      const processed = trimmed.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')

      // List items
      if (trimmed.startsWith('- ') || trimmed.startsWith('* '))
        return `<li style="margin:2px 0;margin-left:20px">${processed.slice(2)}</li>`

      // Empty lines
      if (!trimmed) return '<br/>'

      // Regular paragraphs
      return `<p style="margin:4px 0;text-align:justify">${processed}</p>`
    })
    .join('\n')
}

/**
 * Parse mermaid code blocks from markdown content
 * Source: page-ver-cotizacion.js:37-47 + server.js:199-209
 */
export function parseMermaidBlocks(
  text: string
): { before: string; code: string; after: string }[] {
  const blocks: { before: string; code: string; after: string }[] = []
  const regex = /```mermaid\s*\n([\s\S]*?)```/g
  let lastIndex = 0
  let match

  while ((match = regex.exec(text)) !== null) {
    blocks.push({
      before: text.slice(lastIndex, match.index),
      code: match[1].trim(),
      after: '',
    })
    lastIndex = match.index + match[0].length
  }

  if (blocks.length > 0) {
    blocks[blocks.length - 1].after = text.slice(lastIndex)
  }

  return blocks
}

/**
 * Generate next cotización number
 * Source: app.js:36-41
 */
export function nextCotNumber(existingNumbers: string[]): string {
  if (existingNumbers.length === 0) return '260001'
  const nums = existingNumbers.map((n) => parseInt(n)).filter((n) => !isNaN(n))
  return String(Math.max(...nums) + 1)
}
